/**
 * 記事一覧.
 *
 * What appears here is decided by the server: a clinic admin sees every store in
 * their clinic, a store user only their own. This screen sends no scope of its
 * own and filters nothing by store unless asked - the list it receives is
 * already the list this person may see.
 *
 * The row IS the action. Opening an article lands on the step it stopped at, so
 * the 「続きを書く」 button was a second control that did what clicking the row
 * already did - and it sat beside 削除, which made the destructive one the
 * easiest thing to hit after the link. The row opens; deleting is behind the
 * menu; and the space those buttons took is now the article's keywords, its
 * section progress and when it is meant to go out, which is what somebody scans
 * this list for.
 */
import { confirmDialog } from '../components/AppDialog.tsx';
import { useMemo } from 'react';
import {
  Link,
  useLoaderData,
  useOutletContext,
  useRevalidator,
  useSearchParams,
} from 'react-router';
import { del, type Article, type ListResponse, type Session } from '../api.ts';
import { Card, Empty, Pill, RowLink, RowMenu, Table, jaDate, jaNumber } from '../components/ui.tsx';
import { Select } from '../components/Select.tsx';
import { SkeletonRows, usePendingScreen } from '../components/Skeleton.tsx';
import { AnimatePresence, listItem, m } from '../components/motion.tsx';
import { STAGES, stageSummary } from '../stage.ts';
import { usePaged } from '../paging.ts';

/** The states, in the order an article moves through them. */
const STATUS_LABEL: Record<string, string> = {
  DRAFT: '下書き',
  IN_REVIEW: '要確認',
  APPROVED: '承認済み',
  PUBLISHED: '公開済み',
  ARCHIVED: 'アーカイブ',
};

const STATUS_TONE: Record<string, 'default' | 'ok' | 'warn' | 'muted'> = {
  DRAFT: 'muted',
  IN_REVIEW: 'warn',
  APPROVED: 'ok',
  PUBLISHED: 'ok',
  ARCHIVED: 'muted',
};

/** Rows per request, first page included. Must match the loader's own limit. */
const PAGE = 25;

/** The tables behind this list. A module constant; see the note in paging.ts. */
const TABLES = ['articles', 'stores'] as const;

export default function Articles() {
  const { items } = useLoaderData<ListResponse<Article>>();
  const [params, setParams] = useSearchParams();
  const revalidate = useRevalidator();
  const pending = usePendingScreen();
  const session = useOutletContext<Session>();

  /** Filters live in the URL, so a filtered view survives a reload and can be shared. */
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  /*
   * The stores this person may filter by. A store user has one, and a selector
   * with a single option is a control that cannot do anything - so it only
   * appears for somebody who works across more than one.
   */
  const clinic = session.memberships.find((m) => m.clinicId === session.activeClinicId);
  const stores = clinic?.stores ?? [];

  const filtered = ['q', 'status', 'stage', 'storeId'].some((key) => params.get(key));

  /*
   * Later pages carry the filters, or scrolling a filtered list would append
   * rows from the unfiltered one.
   */
  const query = useMemo(() => {
    const next = new URLSearchParams();
    for (const key of ['q', 'status', 'stage', 'storeId'] as const) {
      const value = params.get(key);
      if (value) next.set(key, value);
    }
    return next.toString();
  }, [params]);

  const paged = usePaged<Article>(
    `/articles${query ? `?${query}` : ''}`,
    items,
    PAGE,
    true,
    TABLES,
  );

  return (
    <Card
      title="記事一覧"
      caption="行を選ぶと、その記事が止まっている工程から開きます。"
      actions={
        <Link className="btn btn--sm" to="/articles/new">
          ＋ 新規記事
        </Link>
      }
    >
      {/*
        Four filters in one row above the list. Search covers the title and the
        keywords: somebody looking for 「医療脱毛」 means the articles written to
        rank for it, and only some of them carry it in the title.
      */}
      <form
        className="filters"
        onSubmit={(event) => {
          event.preventDefault();
          setParam('q', new FormData(event.currentTarget).get('q') as string);
        }}
      >
        <div className="field" style={{ marginBottom: 0, minWidth: 200 }}>
          <label htmlFor="q">検索</label>
          <input
            id="q"
            name="q"
            placeholder="タイトル・キーワード"
            defaultValue={params.get('q') ?? ''}
          />
        </div>
        <div className="field" style={{ marginBottom: 0, minWidth: 150 }}>
          <label htmlFor="status">状態</label>
          <Select
            id="status"
            value={params.get('status') ?? ''}
            onChange={(value) => setParam('status', value)}
            options={[
              { value: '', label: 'すべて' },
              ...['DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'ARCHIVED'].map((status) => ({
                value: status,
                label: STATUS_LABEL[status]!,
              })),
            ]}
          />
        </div>
        <div className="field" style={{ marginBottom: 0, minWidth: 150 }}>
          <label htmlFor="stage">進行</label>
          <Select
            id="stage"
            value={params.get('stage') ?? ''}
            onChange={(value) => setParam('stage', value)}
            options={[
              { value: '', label: 'すべて' },
              { value: 'plan', label: '構成づくり' },
              { value: 'write', label: '本文の執筆中' },
              { value: 'polish', label: '仕上げ' },
            ]}
          />
        </div>
        {stores.length > 1 && (
          <div className="field" style={{ marginBottom: 0, minWidth: 170 }}>
            <label htmlFor="storeId">店舗</label>
            <Select
              id="storeId"
              value={params.get('storeId') ?? ''}
              onChange={(value) => setParam('storeId', value)}
              options={[
                { value: '', label: 'すべての店舗' },
                ...stores.map((store) => ({ value: store.id, label: store.name })),
              ]}
            />
          </div>
        )}
        <div style={{ alignSelf: 'end', display: 'flex', gap: 8 }}>
          <button className="btn btn--sm" type="submit">
            検索
          </button>
          {filtered && (
            <button className="btn btn--ghost btn--sm" type="button" onClick={() => setParams({})}>
              条件をクリア
            </button>
          )}
        </div>
      </form>

      {pending ? (
        <SkeletonRows rows={5} columns={5} />
      ) : paged.items.length === 0 ? (
        <Empty>
          {filtered
            ? '条件に合う記事がありません。条件を変えるか、クリアしてください。'
            : 'まだ記事がありません。「＋ 新規記事」から、店舗とタイトルを決めて始めます。'}
        </Empty>
      ) : (
        <>
          <Table
            head={
              <>
                <th scope="col">タイトル</th>
                {stores.length > 1 && <th scope="col">店舗</th>}
                <th scope="col">進行</th>
                <th scope="col">状態</th>
                <th scope="col">公開想定</th>
                <th scope="col">更新</th>
                <th scope="col" />
              </>
            }
          >
            {/* Rows arrive and leave rather than cutting - see motion.tsx. */}
            {/*
            Default mode, not popLayout: popLayout lifts a leaving item out with
            position:absolute, and a table row taken out of its table loses its
            column widths and squashes. Here it fades where it is, then the rows
            below glide up (listItem's layout spring).
          */}
          <AnimatePresence>
              {paged.items.map((article, index) => {
                /*
                 * Where this one got to, and where opening it should land.
                 *
                 * Derived from the row, never stored: a stage column would be a
                 * second copy of facts these counts already carry.
                 */
                const stage = stageSummary(article);
                const path = STAGES.find((step) => step.id === stage.stage)!.path(article.id);
                const sections = article.section_count ?? 0;
                const drafted = article.drafted_count ?? 0;
                return (
                  <m.tr
                    key={article.id}
                    className="row-link"
                    data-motion
                    layout
                    {...listItem(index)}
                  >
                    <td>
                      <RowLink to={path}>{article.title}</RowLink>
                      {/*
                      The keywords under the title. They are what the article is
                      written to rank for, which is the thing that tells two
                      similar titles apart.
                    */}
                      {article.keywords.length > 0 && (
                        <span className="muted small" style={{ display: 'block' }}>
                          {article.keywords.slice(0, 3).join('・')}
                          {article.keywords.length > 3 && ` ほか${article.keywords.length - 3}`}
                        </span>
                      )}
                    </td>
                    {stores.length > 1 && <td className="muted">{article.store_name}</td>}
                    <td>
                      <Pill tone={stage.stage === 'polish' ? 'ok' : 'muted'}>{stage.label}</Pill>
                      {/* How much of the outline is written, where that is the work left. */}
                      {stage.stage === 'write' && sections > 0 && (
                        <span className="muted small" style={{ display: 'block' }}>
                          {jaNumber(drafted)}/{jaNumber(sections)}節
                        </span>
                      )}
                    </td>
                    <td>
                      <Pill tone={STATUS_TONE[article.status] ?? 'default'}>
                        {STATUS_LABEL[article.status] ?? article.status}
                      </Pill>
                    </td>
                    <td className="muted small">
                      {article.publish_on ? jaDate(article.publish_on) : '—'}
                    </td>
                    <td className="muted small">{jaDate(article.updated_at)}</td>
                    <td>
                      {/*
                      Deleting is permanent and archiving is not, so archiving
                      stays the default on the article's own screen and this menu
                      carries the one that cannot be undone - reachable, and not
                      the easiest target on the row.
                    */}
                      <RowMenu>
                        <Link to={path}>{stage.next}</Link>
                        <button
                          type="button"
                          onClick={() =>
                            void confirmDialog({
                              title: 'この記事を削除しますか？',
                              message: `「${article.title}」を完全に削除します。元に戻せません。`,
                              confirmLabel: '削除する',
                              danger: true,
                            }).then(async (ok) => {
                              if (!ok) return;
                              await del(`/articles/${article.id}/permanent`);
                              await revalidate.revalidate();
                            })
                          }
                        >
                          完全に削除
                        </button>
                      </RowMenu>
                    </td>
                  </m.tr>
                );
              })}
            </AnimatePresence>
          </Table>

          {!paged.done && (
            <div ref={paged.sentinel} className="more-rows">
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                disabled={paged.loading}
                onClick={paged.more}
              >
                {paged.loading ? '読み込み中…' : 'さらに表示'}
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
